import {
    DataTable,
    TikTokTableCol,
    TikTokTableHeadColFilter,
} from 'components/DataVis/DataTable';
import {
    defaultFilterAccessorsAndOptions,
    defaultHandleFilter,
} from 'components/CardsWrapper';
import {
    useAreFlavorFiltersSupported,
    useEndDate,
    useHandleAreFlavorFiltersSupported,
    useIsAdmin,
    useResetSortOptionFlavorUnsupported,
    useSetupFlavorFilters,
} from 'utils/hooks';
import DownloadCSVMenuButton from 'components/Button/DownloadCSVMenuButton';
import ErrorBoundary from 'containers/ErrorBoundary';
import { FLAVORS_METADATA } from 'components/FlavorFilters/utils';
import FilterSelect from 'components/Input/FilterSelect';
import { SectionCard } from 'components/SectionCard';
import Stack from 'components/Stack';
import { createDefaultColumns } from 'components/DataVis/DataTable/utils';
import { getOnChange } from 'components/SectionTableFilterChips';
import { getPriceSegmentationFilter } from 'state/utils';
import { useFilterSearchParamsStateOnly } from 'utils/hooks/useFilter';
import useHandleSearchInputChange from 'utils/hooks/useHandleSearchInputChange';
import useHashtagDeepLink from 'utils/hooks/useHashtagDeepLink';
import { useInfiniteQuery } from 'react-query';
import { useSortSearchParamsStateOnly } from 'utils/hooks/useSort';
import useStaleFilterNotice from 'utils/hooks/useStaleFilterNotice';
import useTableColumns from 'utils/hooks/useTableColumns';

const filterFlavorsColumn = (columns) =>
    columns?.filter((key) => !FLAVORS_METADATA.key.includes(key));

/* The container the preset-chip components provide around the filter row, for
   sources that have no presets to show. Matches their own wrapper: flex, wrap,
   12px gap - without it a source with no chips loses the row's layout. */
const FilterRowOnly = ({ children }) => (
    <div className="flex min-w-0 flex-1 flex-wrap gap-3">{children}</div>
);

/**
 * Builds one source's Explore infinite-scroll table.
 *
 * `usePeriod` names its own keys, so `sendPeriod` gates and sends whatever the
 * source's hook returns - `{ month, year }` reads as naturally as
 * `{ week, year }`.
 *
 * `descendants` sends `category_descendants` in place of `category`, not
 * alongside it. `deepLink` wires the `?hashtag=` URL seam and needs
 * `hashtagDrawerByName` with it: a link can name a hashtag that is not among
 * the fetched rows, which only a drawer that fetches by name can render.
 */
const createExploreInfiniteTable = ({
    queryName,
    queryFn,
    usePeriod,
    healthStatKeys,
    clusterFilterKey,
    clusterPrefix,
    sendPeriod = false,
    descendants = false,
    deepLink = false,
    hashtagDrawerByName = false,
    revealSortedColumn = false,
    priceSegmentation = false,
}) =>
    function ExploreInfiniteTable({
        category,
        categoryQuery,
        country,
        hashtags,
        brands,
        categories,
        categoryDescendants,
        trendDimension,
        paginateBy,
        name,
        tableName,
        NameBodyColComponent,
        hashtagHealthStatFilterOptions,
        healthStatFilterOptions,
        getDownloadOptions,
        source,
        mixpanelMeta,
        filterSelectMenus,
        defaultInitialSortOption,
        PresetFilterChipsComponent = FilterRowOnly,
        DrawerComponent,
        DrawerContentDefaultComponent,
        DrawerContentHashtagComponent,
        filterColumns,
        sortOptions,
        metrics,
    }) {
        const isAdmin = useIsAdmin();
        const { query: periodQuery, ...period } = usePeriod();
        const { week, year } = period;
        const periodValues = Object.values(period);
        const hasPeriod =
            periodValues.length > 0 && periodValues.every(Boolean);
        const date = useEndDate(source);
        const isHashtagData = !!hashtags;
        const flavorFiltersSupported = useAreFlavorFiltersSupported({
            categoryQuery,
        });
        const areFlavorFiltersSupported =
            isHashtagData || brands ? false : flavorFiltersSupported;

        const filterMissingData = false;
        const { handleFilter, filterAccessorsAndOptions } =
            useSetupFlavorFilters({
                defaultHandleFilter,
                defaultFilterAccessorsAndOptions,
            });
        const supportedFilters = Object.keys(handleFilter);
        const {
            filters,
            filtersKey,
            setFilter,
            setFilters,
            resetFilters,
            staleFilters,
        } = useFilterSearchParamsStateOnly({
            filterAccessorsAndOptions,
            initialFilterOption: {},
            supportedFilters,
        });
        useStaleFilterNotice(staleFilters);
        const nameFilter =
            filters && filters.name && filters.name.length
                ? filters.name[0]
                : null;

        const handleSearchInputChange = useHandleSearchInputChange({
            nameFilter,
            setFilter,
        });
        const {
            setSortOption: baseSetSortOption,
            sortOption,
            sortOptionKey,
        } = useSortSearchParamsStateOnly({
            initialSortOption: defaultInitialSortOption,
            options: sortOptions,
            sortOptionKeyAccessor: (sortOption) => sortOption.value,
        });
        // resets flavor filters if set and unsupported
        useHandleAreFlavorFiltersSupported({
            categoryQuery,
            filters,
            setFilters,
        });

        const compositeFilterColumns = (columns) => {
            let filteredColumns = columns;

            if (typeof filterColumns === 'function') {
                filteredColumns = filterColumns(filteredColumns);
            }

            if (!areFlavorFiltersSupported) {
                filteredColumns = filterFlavorsColumn(filteredColumns);
            }

            return filteredColumns;
        };

        const setSupportedSortOption = ({ sortBy, ascending }) =>
            setSortOption(
                sortOptions.find(
                    ({ sortBy: a, ascending: b }) =>
                        sortBy === a && ascending === b,
                ),
            );

        useResetSortOptionFlavorUnsupported({
            categoryQuery,
            areFlavorFiltersSupported,
            sortOptionKey,
            setSupportedSortOption,
            defaultInitialSortOption,
        });
        const healthStatOptions = isHashtagData
            ? hashtagHealthStatFilterOptions
            : healthStatFilterOptions;
        const queryData = {
            paginateBy,
            enabled: !!(
                (!sendPeriod || hasPeriod) &&
                country &&
                (categories ||
                    (descendants && categoryDescendants) ||
                    brands ||
                    hashtags)
            ),
            hashtag: hashtags,
            brand: brands,
            category: categories,
            country: country ? [country.id] : null,
            ...(sendPeriod && period),
            ...(descendants &&
                categoryDescendants && {
                    category: null,
                    category_descendants: categoryDescendants,
                }),
            per_page: 200,
        };
        const { enabled: _, ...initialQueryData } = queryData;
        const priceFilter = priceSegmentation
            ? getPriceSegmentationFilter(filters?.healthStats)
            : undefined;
        const queryDataFilterAndSortConfig = {
            order_by: sortOption?.sortBy,
            ascending: sortOption?.ascending,
            ...(filters?.name?.length && { search: filters.name[0] }),
            ...(filters?.[clusterFilterKey]?.length && {
                cluster: filters[clusterFilterKey].map(({ value }) =>
                    value.replace(clusterPrefix, ''),
                ),
            }),
            ...(priceFilter && { price_segmentation: priceFilter }),
            ...filters?.healthStats
                ?.filter(({ metricLabel }) =>
                    healthStatOptions.some(
                        ({ label }) => label === metricLabel,
                    ),
                )
                .reduce((acc, { metricLabel, score }) => {
                    const key = healthStatKeys[metricLabel];
                    if (!key) return acc;
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(score);
                    return acc;
                }, {}),
            ...(filters?.addedOn?.length && { added_on: filters.addedOn }),
        };
        const queryKey = [
            queryName,
            {
                ...initialQueryData,
                ...queryDataFilterAndSortConfig,
            },
        ];
        const dataQuery = useInfiniteQuery(queryKey, queryFn, {
            enabled: queryData.enabled,
            staleTime: Infinity,
            getNextPageParam: (lastPage) => lastPage?.next_page ?? undefined,
            keepPreviousData: true,
        });
        if (dataQuery.isError) {
            console.error('Error fetching data:', dataQuery.error);
        }

        const MAX_EXPORT_RECORDS = 10000;

        const getQueryData = async () => {
            const currentPages = dataQuery?.data?.pages || [];
            const perPage = queryKey[1]?.per_page || 200;
            const maxPages = Math.ceil(MAX_EXPORT_RECORDS / perPage);
            let firstPage = currentPages[0];

            if (!firstPage) {
                firstPage = await queryFn({ queryKey, pageParam: 1 });
            }

            const cappedLastPage = Math.min(
                firstPage?.last_page || 1,
                maxPages,
            );
            const pages = currentPages.length
                ? currentPages.slice(0, cappedLastPage)
                : [firstPage];
            const startPage = currentPages.length ? currentPages.length + 1 : 2;

            const proms = [];
            for (let i = startPage; i <= cappedLastPage; i++) {
                proms.push(queryFn({ queryKey, pageParam: i }));
            }
            const restPages = await Promise.all(proms);

            return [...pages, ...restPages]
                .map(({ data }) => data)
                .flat()
                .slice(0, MAX_EXPORT_RECORDS);
        };
        const {
            fetchNextPage,
            isFetchingNextPage,
            fetchPreviousPage,
            hasNextPage,
            hasPreviousPage,
            isFetching,
            ...result
        } = dataQuery;

        const rows = (result.data?.pages || []).reduce((acc, { data }) => {
            if (!data) return acc;
            acc.push(...data.map((o) => ({ ...o, relatedName: name })));
            return acc;
        }, []);
        const { drawerProps, linkedHashtag } = useHashtagDeepLink({
            enabled: deepLink && isHashtagData,
            rows,
        });
        const isSuccess = result.isSuccess;
        const isLoading =
            periodQuery.isLoading ||
            result.isLoading ||
            result.isFetchingNextPage;
        const dataReady = isSuccess && rows.length > 0;

        const { visibleOrderedMetrics, columnsMenu, setSortOption } =
            useTableColumns({
                tableName,
                metrics,
                setSortOption: baseSetSortOption,
                // Unhides the sorted column on mount, and saves it into the
                // source's stored column set.
                ...(revealSortedColumn && { sortOption }),
                filterColumns: compositeFilterColumns,
            });
        const visibleColumnsConfig = createDefaultColumns(
            ['name', ...visibleOrderedMetrics],
            { showHealthStatLabels: true },
        );

        visibleColumnsConfig[0].formatCell = ({ row }) => (
            <NameBodyColComponent
                row={row}
                isHashtagData={isHashtagData}
                type={trendDimension}
            />
        );

        const isNoData = !(rows && rows.length);
        let noDataMessage;

        if (!isNoData && isSuccess) {
            noDataMessage = `No ${trendDimension} available to display. ${
                filters && Object.keys(filters).length
                    ? 'Try adjusting the filters'
                    : ''
            }`;
        } else if (isNoData && isLoading) {
            noDataMessage = 'Loading...';
        } else if (isNoData && result.isError) {
            noDataMessage =
                'Could not load the data. Refresh the page to try again.';
        } else if (isNoData) {
            noDataMessage = `No ${paginateBy} for this category.`;
        }
        const downloadOptions = getDownloadOptions({
            entity: name || 'all-industries',
            country,
            date,
            source,
            paginateBy,
            visibleOrderedMetrics,
            getQueryData,
            isAdmin,
        });
        const rowHeight =
            !isHashtagData || trendDimension === 'brands' ? 75 : 50;

        const renderDrawerContent = (selectedId) => {
            const hashtagName = linkedHashtag(selectedId);
            if (hashtagName) {
                // Try to find the data in rows (may or may not exist)
                const dataPoint = rows?.find((row) => row.name === hashtagName);

                return (
                    <ErrorBoundary>
                        <DrawerContentHashtagComponent
                            hashtag={hashtagName}
                            category={categories?.[0] || null}
                            brand={
                                dataPoint
                                    ? dataPoint.brand
                                    : brands
                                      ? '*'
                                      : null
                            }
                            initialData={dataPoint || undefined}
                        />
                    </ErrorBoundary>
                );
            }

            // Normal case - row clicked in table
            const dataPoint =
                selectedId && rows && rows.find(({ id }) => id === selectedId);
            if (!dataPoint) return null;
            const DrawerContentComponent = isHashtagData
                ? DrawerContentHashtagComponent
                : DrawerContentDefaultComponent;
            return (
                <ErrorBoundary>
                    {isHashtagData && hashtagDrawerByName ? (
                        <DrawerContentHashtagComponent
                            hashtag={dataPoint.name}
                            initialData={dataPoint}
                            category={dataPoint.category}
                            brand={dataPoint.brand || (brands ? '*' : null)}
                        />
                    ) : (
                        <DrawerContentComponent
                            paginateBy={paginateBy}
                            dataPoint={dataPoint}
                            parentHierarchies={category?.hierarchies}
                        />
                    )}
                </ErrorBoundary>
            );
        };

        let menus = [...filterSelectMenus];
        if (areFlavorFiltersSupported) menus.push('flavors');
        else if (!areFlavorFiltersSupported)
            menus = menus.filter((menu) => menu !== 'flavors');

        return (
            <SectionCard css="padding-top: 1.25rem;">
                <Stack $space="1.5rem">
                    <Stack
                        $space=".75rem"
                        $direction="row"
                        $wrap={false}
                        css="align-items: flex-start;"
                    >
                        <PresetFilterChipsComponent
                            filters={filters}
                            setFilters={setFilters}
                            sortOption={sortOption}
                            setSortOption={setSupportedSortOption}
                            initialSortOption={defaultInitialSortOption}
                            healthStatOptions={healthStatOptions}
                            week={week}
                            year={year}
                        >
                            <FilterSelect
                                filters={filters}
                                setFilter={setFilter}
                                setFilters={setFilters}
                                resetFilters={resetFilters}
                                sortOption={sortOption}
                                setSortOption={setSupportedSortOption}
                                initialSortOption={defaultInitialSortOption}
                                mixpanelMeta={mixpanelMeta}
                                menus={menus}
                                getOnChange={getOnChange}
                                showAllHealthStats={true}
                                showPresetsMenu={false}
                                healthStatFilterOptions={healthStatOptions}
                                menuAlign="left"
                                css="align-self: flex-start;"
                                data={dataQuery.data}
                                isLoadingData={isLoading}
                                filterMissingData={filterMissingData}
                            />
                        </PresetFilterChipsComponent>
                        <Stack
                            $space=".75rem"
                            $direction="row"
                            css="display: flex; flex-shrink: 0; margin-left: auto;"
                        >
                            <DownloadCSVMenuButton
                                isDisabled={!dataReady}
                                isLoading={isLoading}
                                options={downloadOptions}
                                mixpanelMeta={mixpanelMeta}
                                buttonProps={{ size: 'default' }}
                                css="align-self: flex-start; margin-left: auto;"
                            />
                            {columnsMenu}
                        </Stack>
                    </Stack>
                    <DataTable
                        isLoading={isLoading}
                        isFetching={isFetching}
                        fetchNextPage={fetchNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        data={rows}
                        dataKey={`d-*-s-${sortOptionKey}-f-${filtersKey}`}
                        week={week}
                        year={year}
                        renderDrawerContent={renderDrawerContent}
                        {...drawerProps}
                        columns={visibleColumnsConfig}
                        sortOption={sortOption}
                        setSortOption={setSupportedSortOption}
                        onSearchInputChange={handleSearchInputChange}
                        limitSearchWords={3}
                        name={name}
                        rowHeight={rowHeight}
                        noDataMessage={noDataMessage}
                        DrawerComponent={DrawerComponent}
                        TableCol={TikTokTableCol}
                        TableHeadColFilter={TikTokTableHeadColFilter}
                    />
                </Stack>
            </SectionCard>
        );
    };

export default createExploreInfiniteTable;
